// src/admin/AdminHostelsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';

import { db, storage } from '../firebase';
import AdminJourneyHostels from './AdminJourneyHostels';
import AdminHostelRooms from './AdminHostelRooms';

/* ───────── utilities ───────── */
const HOSTEL_COLL = collection(db, 'hostels');
const CITY_COLL = collection(db, 'cities');
const imgPathUtil = (hostelId, fileName) =>
  `hostelImages/${hostelId}/${uuidv4()}_${fileName}`;

/* ───────── initial template ───────── */
const BLANK_HOSTEL = {
  id: '',
  name: '',
  cityId: '',
  link: '',
  notes: '', // Legacy field - kept for backwards compatibility
  description: '', // New field for Description section
  info: '', // New field for Info section
  contact: '',
  images: [],
  latitude: '',
  longitude: '',
  ratingScore: '',
  ratingCount: '',
  cheapestPrice: '',
  currency: 'USD',
  amenities: [],
  roomOffers: [], // Legacy field - will be replaced by rooms
  amadeusHotelId: '',
  // New fields
  vibe: 'clean', // Default vibe - "clean", "cozy", or "lit"
  cashRequired: false, // Hostel-wide cash payment requirement
  rooms: [],
  hasGenderSpecificRooms: false,
  hasMixedRooms: false,
  totalBeds: 0,
  totalRooms: 0,
  availabilityLastUpdated: null
};

export default function AdminHostelsPage() {
  /* ——— STATE ——— */
  const [hostels, setHostels] = useState([]);
  const [selectedHostel, setSelectedHostel] = useState(BLANK_HOSTEL);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRoomManagement, setShowRoomManagement] = useState(false);

  /* ——— FETCH ——— */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const [hSnap, cSnap] = await Promise.all([
          getDocs(HOSTEL_COLL),
          getDocs(CITY_COLL),
        ]);
        if (!alive) return;

        const cityMap = {};
        cSnap.forEach((d) => (cityMap[d.id] = d.data().name));

        setCities(
          cSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
        );

        setHostels(
          hSnap.docs.map((d) => {
            const data = d.data();
            return {
              id: d.id,
              cityName: cityMap[data.cityId] || 'Unknown',
              ...data,
              images: Array.isArray(data.images) ? data.images : [],
              amenities: Array.isArray(data.amenities) ? data.amenities : [],
              roomOffers: Array.isArray(data.roomOffers) ? data.roomOffers : [],
              rooms: Array.isArray(data.rooms) ? data.rooms : [],
              description: data.description || data.notes || '', // Fall back to notes for existing data
              info: data.info || '',
              vibe: data.vibe || 'clean', // Default existing hostels to 'clean'
              cashRequired: data.cashRequired || false, // Default to false if not set
              hasGenderSpecificRooms: data.hasGenderSpecificRooms || false,
              hasMixedRooms: data.hasMixedRooms || false,
              totalBeds: data.totalBeds || 0,
              totalRooms: data.totalRooms || 0,
              availabilityLastUpdated: data.availabilityLastUpdated || null
            };
          }).sort((a, b) => a.name.localeCompare(b.name))
        );
        setError('');
      } catch (e) {
        console.error(e);
        if (alive) setError('Firestore read failed. Please try refreshing.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  /* ——— FORM HANDLERS ——— */
  const onInput = useCallback((e) => {
    const { name, value } = e.target;
    setSelectedHostel((p) => ({ ...p, [name]: value }));
  }, []);

  const onEdit = useCallback((hostelData) => {
    const hostelToEdit = {
        ...BLANK_HOSTEL,
        ...hostelData,
        images: Array.isArray(hostelData.images) ? hostelData.images : [],
        amenities: Array.isArray(hostelData.amenities) ? hostelData.amenities : [],
        roomOffers: Array.isArray(hostelData.roomOffers) ? hostelData.roomOffers : [],
        rooms: Array.isArray(hostelData.rooms) ? hostelData.rooms : [],
        description: hostelData.description || hostelData.notes || '', // Fall back to notes for existing data
        info: hostelData.info || '',
        vibe: hostelData.vibe || 'clean', // Ensure vibe has a default
        cashRequired: hostelData.cashRequired || false, // Ensure cashRequired has a default
    };
    setSelectedHostel(hostelToEdit);
    setShowRoomManagement(false);
  }, []);

  /* ——— MAIN CRUD OPERATIONS ——— */
  const onDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this hostel? This will also delete associated images from storage and cannot be undone.')) return;
    
    setLoading(true);
    setError('');
    try {
      const hostelToDelete = hostels.find(h => h.id === id);
      if (hostelToDelete && hostelToDelete.images && hostelToDelete.images.length > 0) {
        for (const imageUrl of hostelToDelete.images) {
          try {
            const urlRef = ref(storage, imageUrl);
            await deleteObject(urlRef);
          } catch (imgErr) {
            console.warn(`Failed to delete image ${imageUrl} during hostel deletion:`, imgErr);
          }
        }
      }
      
      // Also delete room images if any
      if (hostelToDelete && hostelToDelete.rooms) {
        for (const room of hostelToDelete.rooms) {
          if (room.photos && room.photos.length > 0) {
            for (const photoUrl of room.photos) {
              try {
                const urlRef = ref(storage, photoUrl);
                await deleteObject(urlRef);
              } catch (imgErr) {
                console.warn(`Failed to delete room photo ${photoUrl}:`, imgErr);
              }
            }
          }
        }
      }
      
      await deleteDoc(doc(db, 'hostels', id));
      setHostels((arr) => arr.filter((h) => h.id !== id));
      if (selectedHostel.id === id) {
        setSelectedHostel(BLANK_HOSTEL);
        setShowRoomManagement(false);
      }
      alert('Hostel deleted successfully.');
    } catch (e) {
      console.error('Hostel delete failed:', e);
      setError(`Hostel delete failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const onSave = async (hostelDataToSave) => {
    setLoading(true);
    setError('');
    try {
      const coerceNum = (v) => (v === '' || v === undefined || v === null ? null : Number(v));
      const processedData = {
        ...hostelDataToSave,
        images: Array.isArray(hostelDataToSave.images) ? hostelDataToSave.images : [],
        amenities: typeof hostelDataToSave.amenities === 'string'
            ? hostelDataToSave.amenities.split(',').map((s) => s.trim()).filter(Boolean)
            : (Array.isArray(hostelDataToSave.amenities) ? hostelDataToSave.amenities : []),
        roomOffers: typeof hostelDataToSave.roomOffers === 'string'
            ? JSON.parse(hostelDataToSave.roomOffers || '[]')
            : (Array.isArray(hostelDataToSave.roomOffers) ? hostelDataToSave.roomOffers : []),
        rooms: Array.isArray(hostelDataToSave.rooms) ? hostelDataToSave.rooms : [],
        description: hostelDataToSave.description || hostelDataToSave.notes || '', // Save description field
        info: hostelDataToSave.info || '', // Save info field
        latitude: coerceNum(hostelDataToSave.latitude),
        longitude: coerceNum(hostelDataToSave.longitude),
        ratingScore: coerceNum(hostelDataToSave.ratingScore),
        ratingCount: coerceNum(hostelDataToSave.ratingCount),
        cheapestPrice: coerceNum(hostelDataToSave.cheapestPrice),
        vibe: hostelDataToSave.vibe || 'clean', // Ensure vibe is saved
        cashRequired: hostelDataToSave.cashRequired || false, // Ensure cashRequired is saved
        hasGenderSpecificRooms: hostelDataToSave.hasGenderSpecificRooms || false,
        hasMixedRooms: hostelDataToSave.hasMixedRooms || false,
        totalBeds: hostelDataToSave.totalBeds || 0,
        totalRooms: hostelDataToSave.totalRooms || 0,
        availabilityLastUpdated: hostelDataToSave.availabilityLastUpdated || null
      };

      if (!processedData.cityName && processedData.cityId && cities.length > 0) {
        const city = cities.find(c => c.id === processedData.cityId);
        if (city) processedData.cityName = city.name;
        else processedData.cityName = 'Unknown';
      }

      let savedHostelData;

      if (!processedData.id) {
        const { id, ...dataForCreation } = processedData;
        const docRef = await addDoc(HOSTEL_COLL, dataForCreation);
        savedHostelData = { ...dataForCreation, id: docRef.id };
        setHostels((arr) => [...arr, savedHostelData].sort((a, b) => a.name.localeCompare(b.name)));
      } else {
        const hostelId = processedData.id;
        const { id, ...dataForUpdate } = processedData;
        await updateDoc(doc(db, 'hostels', hostelId), dataForUpdate);
        savedHostelData = { ...dataForUpdate, id: hostelId };
        setHostels((arr) => arr.map((h) => (h.id === hostelId ? savedHostelData : h)).sort((a, b) => a.name.localeCompare(b.name)));
      }
      
      setSelectedHostel(savedHostelData);
      alert('Hostel saved successfully.');
      return savedHostelData;
    } catch (e) {
      console.error('Hostel save failed:', e);
      setError(`Hostel save failed: ${e.message}. Check console for details.`);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  /* ——— IMAGE HANDLERS ——— */
  const _uploadImageFileToStorage = async (file, hostelId) => {
    if (!hostelId) throw new Error('Hostel ID is required to upload an image.');
    if (!file) throw new Error('File is required for upload.');
    try {
      const storageRef = ref(storage, imgPathUtil(hostelId, file.name));
      await uploadBytes(storageRef, file);
      return await getDownloadURL(storageRef);
    } catch (e) {
      console.error('Storage image upload failed:', e);
      throw new Error(`Storage image upload failed for ${file.name}: ${e.message}`);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) {
      setError("No file selected for upload.");
      return;
    }

    setLoading(true);
    setError('');
    let currentHostelId = selectedHostel.id;
    let hostelDataForImageUpdate = { ...selectedHostel };

    if (!currentHostelId) {
      try {
        if (!selectedHostel.name || !selectedHostel.cityId) {
            setError("Hostel name and city are required. Please save the hostel details before adding images.");
            setLoading(false);
            return;
        }
        const savedHostel = await onSave(selectedHostel);
        if (!savedHostel || !savedHostel.id) {
          throw new Error("Failed to save new hostel or obtain an ID for image association.");
        }
        currentHostelId = savedHostel.id;
        hostelDataForImageUpdate = savedHostel;
      } catch (saveError) {
        if (!error && saveError.message) setError(`Auto-save before image upload failed: ${saveError.message}`);
        setLoading(false);
        return;
      }
    }

    try {
      const downloadUrl = await _uploadImageFileToStorage(file, currentHostelId);
      const updatedImages = [...(hostelDataForImageUpdate.images || []), downloadUrl];
      
      await updateDoc(doc(db, 'hostels', currentHostelId), { images: updatedImages });
      
      const finalUpdatedHostelState = { ...hostelDataForImageUpdate, id: currentHostelId, images: updatedImages };
      setSelectedHostel(finalUpdatedHostelState);
      setHostels(prevHostels => prevHostels.map(h => 
        h.id === currentHostelId ? finalUpdatedHostelState : h
      ).sort((a,b) => a.name.localeCompare(b.name)));
      alert('Image uploaded and linked successfully.');
    } catch (uploadError) {
      console.error('Image upload or hostel update failed:', uploadError);
      setError(`Image upload failed: ${uploadError.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteImage = async (hostelId, imageUrl) => { 
    if (!hostelId || !imageUrl) {
      setError('Hostel ID and Image URL are required for deletion.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this image? This action is permanent.')) return;

    setLoading(true);
    setError('');
    try {
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);

      const hostelDocRef = doc(db, 'hostels', hostelId);
      const hostelSnap = await getDoc(hostelDocRef);
      if (!hostelSnap.exists()) throw new Error("Hostel document not found in Firestore.");
      
      const currentHostelData = hostelSnap.data();
      const updatedImages = (currentHostelData.images || []).filter((u) => u !== imageUrl);
      
      await updateDoc(hostelDocRef, { images: updatedImages });

      const updatedHostelForState = { ...currentHostelData, id: hostelId, images: updatedImages };
      
      if (selectedHostel.id === hostelId) {
        setSelectedHostel(updatedHostelForState);
      }
      setHostels(prevHostels => prevHostels.map(h =>
        h.id === hostelId ? updatedHostelForState : h
      ).sort((a,b) => a.name.localeCompare(b.name)));
      alert('Image deleted successfully.');
    } catch (e) {
      console.error('Image delete failed:', e);
      setError(`Image delete failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  /* ——— RENDER ——— */
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  
  if (loading && hostels.length === 0 && !error) return <div style={{ padding: isMobile ? 15 : 24 }}>Loading hostels data…</div>;
  if (error && hostels.length === 0) return <div style={{ padding: isMobile ? 15 : 24, color: 'red' }}>ERROR: {error}</div>;

  return (
    <div style={{ 
      padding: 0,
      width: '100%',
      minWidth: 0,
      maxWidth: '100%',
      boxSizing: 'border-box'
    }}>
      <h2 style={{ 
        fontSize: isMobile ? '18px' : '24px', 
        marginBottom: isMobile ? '12px' : '20px',
        margin: isMobile ? '0 0 12px 0' : '0 0 20px 0'
      }}>
        Manage Hostels
      </h2>
      {loading && <p style={{ color: 'blue', fontSize: isMobile ? '13px' : '16px' }}>Processing...</p>}
      {error && hostels.length > 0 && <p style={{ color: 'red', fontSize: isMobile ? '13px' : '16px' }}>ERROR: {error}</p>}

      <AdminJourneyHostels
        hostels={hostels}
        selectedHostel={selectedHostel}
        cities={cities}
        BLANK_HOSTEL={BLANK_HOSTEL}
        handleHostelInputChange={onInput}
        handleEditHostel={onEdit}
        handleSetSelectedHostel={setSelectedHostel}
        handleSaveHostelChanges={onSave}
        handleDeleteHostel={onDelete}
        handleUploadHostelImage={handleImageUpload}
        handleDeleteHostelImage={handleDeleteImage}
        isMobile={isMobile}
      />

      {/* Room Management Section */}
      {selectedHostel.id && (
        <>
          <button
            onClick={() => setShowRoomManagement(!showRoomManagement)}
            style={{
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              padding: isMobile ? '6px 10px' : '10px 15px',
              cursor: 'pointer',
              marginTop: isMobile ? '6px' : '10px',
              marginBottom: isMobile ? '12px' : '20px',
              borderRadius: '3px',
              fontSize: isMobile ? '13px' : '16px',
              width: isMobile ? '100%' : 'auto'
            }}
          >
            {showRoomManagement ? 'Hide' : 'Manage'} Rooms ({selectedHostel.totalRooms || 0} rooms, {selectedHostel.totalBeds || 0} beds)
          </button>
          
          {showRoomManagement && (
            <AdminHostelRooms 
              hostel={selectedHostel}
              onUpdate={(updatedHostel) => {
                setSelectedHostel(updatedHostel);
                setHostels(prev => prev.map(h => 
                  h.id === updatedHostel.id ? updatedHostel : h
                ).sort((a, b) => a.name.localeCompare(b.name)));
              }}
            />
          )}
        </>
      )}
    </div>
  );
}